import { MagnifyingGlassIcon, XMarkIcon, UserIcon } from '@heroicons/react/24/outline'
import { useState, useEffect, useRef } from 'react'
import { useFetcher, useRevalidator } from 'react-router'
import type { SelectedB2BCustomer } from '~/routes/_layout'

type SearchResult = {
  id: string
  companyName: string | null
  firstName: string
  lastName: string
  email: string
  invoiceEmail: string | null
}

export function B2BCustomerSelector({ selectedCustomer }: { selectedCustomer: SelectedB2BCustomer }) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [results, setResults] = useState<SearchResult[]>([])
  const searchFetcher = useFetcher<{ results: SearchResult[] }>()
  const selectFetcher = useFetcher()
  const containerRef = useRef<HTMLDivElement>(null)
  const revalidator = useRevalidator()

  // Load recent customers on focus (when query is empty)
  useEffect(() => {
    if (isOpen && query.length === 0) {
      searchFetcher.load(`/api/b2b-customers/search`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only trigger on isOpen change, not query changes
  }, [isOpen])

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      if (query.length === 0 && isOpen) {
        // Show recent customers when empty
        searchFetcher.load(`/api/b2b-customers/search`)
      }
      return
    }

    const timeout = setTimeout(() => {
      searchFetcher.load(`/api/b2b-customers/search?q=${encodeURIComponent(query)}`)
    }, 300)

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounced search only depends on query
  }, [query])

  // Update results when search fetcher returns
  useEffect(() => {
    if (searchFetcher.data?.results) {
      setResults(searchFetcher.data.results)
    }
  }, [searchFetcher.data])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (customer: SearchResult | null) => {
    selectFetcher.submit(
      { customerId: customer?.id ?? null },
      {
        method: 'POST',
        action: '/api/b2b-customers/select',
        encType: 'application/json'
      }
    )
    setQuery('')
    setResults([])
    setIsOpen(false)
  }

  // Revalidate loader data when customer selection completes to update cart and pricing
  useEffect(() => {
    if (selectFetcher.state === 'idle' && selectFetcher.data) {
      revalidator.revalidate()
    }
  }, [selectFetcher.state, selectFetcher.data, revalidator])

  const displayName = selectedCustomer
    ? selectedCustomer.companyName
      ? `${selectedCustomer.companyName} (${selectedCustomer.firstName} ${selectedCustomer.lastName})`
      : `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
    : null

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center justify-end gap-2">
        {selectedCustomer ? (
          <div className="flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-sm">
            <UserIcon className="h-4 w-4 text-blue-600" />
            <span className="font-medium text-blue-900">{displayName}</span>
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className="ml-1 cursor-pointer text-blue-600 hover:text-blue-800"
              aria-label="Clear B2B customer selection">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={e => {
                setQuery(e.target.value)
                setIsOpen(true)
              }}
              onFocus={() => setIsOpen(true)}
              placeholder="Zoek B2B klant..."
              className="w-64 rounded-lg border border-gray-300 bg-white px-3 py-1.5 pl-9 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
            />
            <MagnifyingGlassIcon className="absolute top-2 left-2.5 h-4 w-4 text-gray-400" />
          </div>
        )}
      </div>

      {/* Search results dropdown */}
      {isOpen && !selectedCustomer && (
        <div className="absolute top-full left-0 z-50 mt-1 w-96 rounded-lg border border-gray-300 bg-white shadow-lg">
          {searchFetcher.state === 'loading' ? (
            <div className="p-4 text-center text-sm text-gray-500">Zoeken...</div>
          ) : results.length > 0 ? (
            <ul className="max-h-64 overflow-y-auto">
              {results.map((customer, index) => (
                <li key={customer.id} className={index > 0 && results.length > 1 ? 'border-t border-gray-200' : ''}>
                  <button
                    type="button"
                    onClick={() => handleSelect(customer)}
                    className="w-full cursor-pointer px-4 py-3 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none">
                    <div className="font-medium text-gray-900">
                      {customer.companyName ? (
                        <>
                          {customer.companyName}
                          <span className="ml-2 text-sm font-normal text-gray-600">
                            ({customer.firstName} {customer.lastName})
                          </span>
                        </>
                      ) : (
                        `${customer.firstName} ${customer.lastName}`
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{customer.email}</div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4 text-center text-sm text-gray-500">Geen resultaten gevonden</div>
          )}
        </div>
      )}
    </div>
  )
}
